import { useCallback, useState } from "react";
import { useQuery, useQueryClient } from "react-query";

import { getHostBookings, completeBooking, cancelBooking } from "apis";
import { Loading } from "components";
import { useAppModal } from "components/common/AppModalProvider";
import { BookingStatus } from "types";
import { BookingDTO } from "types/DTOs/booking/booking.dto";
import { getImageUrl } from "utils/image.util";

const statusConfig: Record<string, { label: string; color: string }> = {
  [BookingStatus.PENDING]: { label: "대기", color: "bg-yellow-100 text-yellow-700" },
  [BookingStatus.PAID]: { label: "결제완료", color: "bg-green-100 text-green-700" },
  [BookingStatus.COMPLETED]: { label: "완료", color: "bg-achromatic-100 text-achromatic-500" },
  [BookingStatus.CANCELLED]: { label: "취소", color: "bg-red-100 text-red-500" },
};

const CANCEL_PRESETS = [
  "급격한 컨디션 저하 및 건강 문제",
  "갑작스러운 회사 업무/야근 발생",
  "인터넷 및 통신 기기 장애",
];

interface HostBookingCardProps {
  booking: BookingDTO;
  onComplete: (id: string) => void;
  onCancel: (id: string) => void;
}

const HostBookingCard = ({ booking, onComplete, onCancel }: HostBookingCardProps) => {
  const status = statusConfig[booking.status] ?? statusConfig[BookingStatus.PENDING];

  return (
    <div className="flex flex-col bg-white rounded-xl border border-achromatic-100 overflow-hidden">
      <div className="flex justify-between items-start p-4 pb-2">
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-black line-clamp-1">
            {booking.service?.title ?? "서비스"}
          </h3>
          <div className="flex items-center gap-2 mt-1">
            {booking.guest?.profileImage ? (
              <img
                src={getImageUrl(booking.guest.profileImage)}
                alt="guest"
                className="w-5 h-5 rounded-full object-cover"
              />
            ) : (
              <div className="w-5 h-5 bg-achromatic-200 rounded-full" />
            )}
            <span className="text-xs text-achromatic-400">
              {booking.guest?.name ?? "게스트"}
            </span>
          </div>
        </div>
        <span className={`text-[10px] font-semibold px-2 py-1 rounded-md ${status.color}`}>
          {status.label}
        </span>
      </div>

      <div className="flex items-center gap-4 px-4 py-2 text-xs text-achromatic-500">
        <span>📅 {booking.schedule?.date ?? "-"}</span>
        <span>
          ⏰ {booking.schedule?.startTime?.slice(0, 5) ?? "--:--"} ~{" "}
          {booking.schedule?.endTime?.slice(0, 5) ?? "--:--"}
        </span>
        <span className="text-primary-500 font-semibold">
          {booking.paidAmount?.toLocaleString() ?? 0}원
        </span>
      </div>

      <div className="flex gap-2 px-4 py-3 border-t border-achromatic-50 bg-achromatic-50/30">
        {booking.status === BookingStatus.PAID && (
          <>
            <button
              onClick={() => onComplete(booking.id)}
              disabled={booking.completedByHost}
              className={`flex-1 py-2 rounded-lg text-xs font-semibold transition-colors ${
                booking.completedByHost
                  ? "bg-achromatic-100 text-achromatic-300"
                  : "bg-primary-500 text-white hover:bg-primary-600"
              }`}
            >
              {booking.completedByHost ? "✅ 완료 확인됨" : "서비스 완료 확인"}
            </button>
            <button
              onClick={() => onCancel(booking.id)}
              className="flex-1 py-2 rounded-lg text-xs font-semibold bg-red-50 text-red-500 hover:bg-red-100 transition-colors"
            >
              예약 취소
            </button>
          </>
        )}
        {booking.status === BookingStatus.COMPLETED && (
          <span className="text-xs text-achromatic-400 py-2">✅ 정산 완료</span>
        )}
        {booking.status === BookingStatus.CANCELLED && (
          <div className="flex flex-col gap-1">
            <span className="text-xs text-achromatic-400">❌ 취소됨</span>
            {booking.cancelReason && (
              <span className="text-[11px] text-achromatic-300">사유: {booking.cancelReason}</span>
            )}
          </div>
        )}
        {booking.status === BookingStatus.PENDING && (
          <span className="text-xs text-achromatic-400 py-2">⏳ 게스트 결제 대기</span>
        )}
      </div>
    </div>
  );
};

/* ── Cancel Reason Modal ── */
interface CancelModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  isLoading: boolean;
}

const CancelReasonModal = ({ isOpen, onClose, onConfirm, isLoading }: CancelModalProps) => {
  const [selected, setSelected] = useState<number | null>(null);
  const [customReason, setCustomReason] = useState("");

  if (!isOpen) return null;

  const isCustom = selected === CANCEL_PRESETS.length;
  const canSubmit = selected !== null && (!isCustom || customReason.trim().length > 0);
  const finalReason = isCustom ? customReason.trim() : (selected !== null ? CANCEL_PRESETS[selected] : "");

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-[400px] mx-4 animate-fadeIn" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 pt-5 pb-3">
          <h3 className="text-base font-extrabold text-black">예약 취소 사유</h3>
          <p className="text-xs text-achromatic-400 mt-1">취소 사유를 선택해주세요. 게스트에게 전달됩니다.</p>
        </div>

        <div className="flex flex-col gap-2 px-5 pb-3">
          {CANCEL_PRESETS.map((preset, i) => (
            <label
              key={i}
              className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                selected === i ? "border-red-400 bg-red-50" : "border-achromatic-100 hover:bg-achromatic-50"
              }`}
            >
              <input
                type="radio"
                name="cancel-reason"
                checked={selected === i}
                onChange={() => setSelected(i)}
                className="accent-red-500"
              />
              <span className="text-sm text-achromatic-700">{preset}</span>
            </label>
          ))}
          <label
            className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
              isCustom ? "border-red-400 bg-red-50" : "border-achromatic-100 hover:bg-achromatic-50"
            }`}
          >
            <input
              type="radio"
              name="cancel-reason"
              checked={isCustom}
              onChange={() => setSelected(CANCEL_PRESETS.length)}
              className="accent-red-500"
            />
            <span className="text-sm text-achromatic-700">기타 (직접 입력)</span>
          </label>
          {isCustom && (
            <input
              className="px-3 py-2 border border-achromatic-200 rounded-xl text-sm focus:outline-none focus:border-red-300"
              placeholder="취소 사유를 입력하세요 (30자 이내)"
              maxLength={30}
              value={customReason}
              onChange={(e) => setCustomReason(e.target.value)}
              autoFocus
            />
          )}
        </div>

        <div className="bg-yellow-50 mx-5 mb-3 px-3 py-2 rounded-lg">
          <p className="text-[11px] text-yellow-700">
            ⚠️ 호스트 취소는 누적 기록되며, 3회 이상 시 서비스 노출이 제한됩니다.
            취소 시 게스트에게 100% 환불이 즉시 진행됩니다.
          </p>
        </div>

        <div className="flex gap-2 px-5 pb-5">
          <button
            onClick={onClose}
            className="flex-1 py-3 rounded-xl text-sm font-semibold bg-achromatic-100 text-achromatic-500 hover:bg-achromatic-200 transition-colors"
          >
            돌아가기
          </button>
          <button
            onClick={() => canSubmit && onConfirm(finalReason)}
            disabled={!canSubmit || isLoading}
            className={`flex-1 py-3 rounded-xl text-sm font-semibold transition-colors ${
              canSubmit && !isLoading
                ? "bg-red-500 text-white hover:bg-red-600"
                : "bg-achromatic-100 text-achromatic-300"
            }`}
          >
            {isLoading ? "취소 처리 중..." : "취소하기"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── Main Component ── */
const HostBookingComponent = () => {
  const queryClient = useQueryClient();
  const { showAlert } = useAppModal();
  const [cancelTargetId, setCancelTargetId] = useState<string | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);

  const {
    data: bookingsData,
    isLoading,
    isError,
  } = useQuery("hostBookings", getHostBookings);

  const bookings: BookingDTO[] = bookingsData?.data?.data ?? [];

  const handleComplete = useCallback(
    async (bookingId: string) => {
      try {
        await completeBooking(bookingId);
        queryClient.invalidateQueries("hostBookings");
      } catch (error) {
        console.error("Complete failed:", error);
        showAlert("완료 처리 중 오류가 발생했습니다.", { variant: 'error' });
      }
    },
    [queryClient]
  );

  const handleCancelConfirm = useCallback(
    async (reason: string) => {
      if (!cancelTargetId) return;
      setIsCancelling(true);
      try {
        await cancelBooking(cancelTargetId, reason);
        queryClient.invalidateQueries("hostBookings");
        setCancelTargetId(null);
        showAlert("예약이 취소되었습니다. 게스트에게 환불이 진행됩니다.", { variant: 'success' });
      } catch (error: any) {
        const msg = error?.response?.data?.message || "취소 처리 중 오류가 발생했습니다.";
        showAlert(msg, { variant: 'error' });
      } finally {
        setIsCancelling(false);
      }
    },
    [cancelTargetId, queryClient]
  );

  if (isLoading) return <Loading />;
  if (isError)
    return (
      <div className="text-center py-10 text-achromatic-400">
        예약 현황을 불러오는 중 오류가 발생했습니다.
      </div>
    );

  if (bookings.length === 0) {
    return (
      <div className="flex flex-col items-center py-16 gap-3 text-achromatic-300">
        <div className="text-4xl">📊</div>
        <p className="font-medium">예약 현황이 없습니다</p>
        <p className="text-sm">게스트가 서비스를 예약하면 여기에 표시됩니다.</p>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col gap-3 p-5">
        {bookings.map((booking) => (
          <HostBookingCard
            key={booking.id}
            booking={booking}
            onComplete={handleComplete}
            onCancel={(id) => setCancelTargetId(id)}
          />
        ))}
      </div>
      <CancelReasonModal
        isOpen={!!cancelTargetId}
        onClose={() => setCancelTargetId(null)}
        onConfirm={handleCancelConfirm}
        isLoading={isCancelling}
      />
    </>
  );
};

export default HostBookingComponent;
